import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  LocalOffer as LocalOfferIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Collections as CollectionsIcon,
  CloudUpload as CloudUploadIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import ProfileSettingsForm from '../components/settings/ProfileSettingsForm';
import PasswordSettingsForm from '../components/settings/PasswordSettingsForm';
import OffersTable from '../components/settings/OffersTable';
import { heroSlideService, api, settingsService } from '../services';

const FALLBACK_SLIDES = [
  {
    id: 'fallback-1',
    title: 'Elevate Your Natural Beauty',
    subtitle: 'Discover our premium collection of luxury eyelashes and beauty essentials',
    cta: 'Shop Collection',
    image: '/luxury-eyelashes-model-close-up-beauty.jpg',
    isActive: true,
  },
  {
    id: 'fallback-2',
    title: 'New Arrivals',
    subtitle: 'Introducing our latest collection of handcrafted premium lashes',
    cta: 'Explore New',
    image: '/beauty-model-dramatic-eyelashes-luxury.jpg',
    isActive: true,
  },
  {
    id: 'fallback-3',
    title: 'Limited Edition',
    subtitle: 'Exclusive designs for the most discerning beauty enthusiasts',
    cta: 'Shop Limited',
    image: '/luxury-cosmetics-eyelashes-premium-beauty.jpg',
    isActive: true,
  },
];

const resolveAssetUrl = (path) => {
  if (!path) {
    return '';
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const baseApiUrl = api?.defaults?.baseURL || 'http://localhost:5000/api';
  const assetBase = baseApiUrl.replace(/\/api\/?$/, '');

  return `${assetBase}/${path.replace(/\\/g, '/')}`;
};

const defaultFormState = {
  title: '',
  subtitle: '',
  cta: '',
  image: '',
  order: 0,
  isActive: true,
};

const HeroSectionManager = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState(defaultFormState);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const fileInputRef = useRef(null);
  const [editingSlide, setEditingSlide] = useState(null);

  const loadSlides = useCallback(async () => {
    try {
      setLoading(true);
      const result = await heroSlideService.getSlides({
        sortBy: 'order',
        sortOrder: 'asc',
      });
      const fetchedSlides = Array.isArray(result?.data) ? result.data : [];
      setSlides(fetchedSlides);
    } catch (error) {
      console.error('Failed to load hero slides:', error);
      toast.error(error?.response?.data?.message || 'Failed to load hero slides');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSlides();
  }, [loadSlides]);

  const sortedSlides = useMemo(() => {
    if (!Array.isArray(slides) || slides.length === 0) {
      return [];
    }

    return [...slides].sort((a, b) => {
      const orderA = Number.isFinite(a?.order) ? a.order : 0;
      const orderB = Number.isFinite(b?.order) ? b.order : 0;
      if (orderA === orderB) {
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      }
      return orderA - orderB;
    });
  }, [slides]);

  const resetForm = useCallback(() => {
    setFormState(defaultFormState);
    setFormErrors({});
    setEditingSlide(null);
  }, []);

  const handleDialogClose = () => {
    if (saving || uploading) {
      return;
    }
    setDialogOpen(false);
    resetForm();
  };

  const openCreateDialog = () => {
    resetForm();
    setFormState({
      ...defaultFormState,
      order: sortedSlides.length,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (slide) => {
    setEditingSlide(slide);
    setFormState({
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      cta: slide.cta || '',
      image: slide.image || '',
      order: Number.isFinite(slide.order) ? slide.order : 0,
      isActive: slide.isActive !== undefined ? Boolean(slide.isActive) : true,
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const validateForm = useCallback((values) => {
    const errors = {};

    if (!values.title || !values.title.trim()) {
      errors.title = 'Title is required';
    }

    if (!values.subtitle || !values.subtitle.trim()) {
      errors.subtitle = 'Subtitle is required';
    }

    if (!values.cta || !values.cta.trim()) {
      errors.cta = 'CTA text is required';
    }

    if (!values.image || !values.image.trim()) {
      errors.image = 'Image is required';
    }

    const orderValue = Number(values.order);
    if (!Number.isFinite(orderValue) || orderValue < 0) {
      errors.order = 'Order must be a non-negative number';
    }

    return errors;
  }, []);

  const handleFormChange = (field) => (event) => {
    const value =
      field === 'isActive' ? event.target.checked : event.target.value;
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setUploading(true);
      const result = await heroSlideService.uploadImage(file);
      const uploadedPath =
        result?.data?.path || result?.data?.url || result?.path || result?.url;
      if (!uploadedPath) {
        throw new Error('Invalid upload response');
      }

      setFormState((prev) => ({
        ...prev,
        image: uploadedPath,
      }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error(error?.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedValues = {
      ...formState,
      title: formState.title.trim(),
      subtitle: formState.subtitle.trim(),
      cta: formState.cta.trim(),
      image: formState.image.trim(),
      order: Number(formState.order),
    };

    const errors = validateForm(trimmedValues);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setSaving(true);
      if (editingSlide?._id) {
        await heroSlideService.updateSlide(editingSlide._id, trimmedValues);
        toast.success('Hero slide updated successfully');
      } else {
        await heroSlideService.createSlide(trimmedValues);
        toast.success('Hero slide created successfully');
      }

      setDialogOpen(false);
      resetForm();
      await loadSlides();
    } catch (error) {
      console.error('Failed to save hero slide:', error);
      toast.error(error?.response?.data?.message || 'Failed to save hero slide');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slide) => {
    if (!slide?._id) {
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete "${slide.title}"?`
    );

    if (!confirmed) {
      return;
    }

    try {
      await heroSlideService.deleteSlide(slide._id);
      toast.success('Hero slide deleted successfully');
      await loadSlides();
    } catch (error) {
      console.error('Failed to delete hero slide:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete hero slide');
    }
  };

  const handleToggleActive = async (slide) => {
    if (!slide?._id) {
      return;
    }

    try {
      await heroSlideService.updateSlide(slide._id, { isActive: !slide.isActive });
      await loadSlides();
    } catch (error) {
      console.error('Failed to update slide status:', error);
      toast.error(error?.response?.data?.message || 'Failed to update slide status');
    }
  };

  const handleReorder = async (currentIndex, direction) => {
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= sortedSlides.length) {
      return;
    }

    const reordered = [...sortedSlides];
    const [movedSlide] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, movedSlide);

    try {
      setReordering(true);
      await Promise.all(
        reordered.map((slide, index) =>
          heroSlideService.updateSlide(slide._id, { order: index })
        )
      );
      await loadSlides();
    } catch (error) {
      console.error('Failed to reorder slides:', error);
      toast.error(error?.response?.data?.message || 'Failed to reorder slides');
    } finally {
      setReordering(false);
    }
  };

  const renderSlidesTable = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={180}
        >
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (sortedSlides.length === 0) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={1}
          py={4}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            No hero slides configured yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            The storefront will use the default fallback slides until you add your own.
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>Order</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Subtitle</TableCell>
              <TableCell>CTA</TableCell>
              <TableCell>Image</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSlides.map((slide, index) => (
              <TableRow key={slide._id || slide.id}>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {Number.isFinite(slide.order) ? slide.order : index}
                    </Typography>
                    <Stack direction="column" spacing={0.5}>
                      <Tooltip title="Move up">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleReorder(index, 'up')}
                            disabled={index === 0 || reordering}
                          >
                            <ArrowUpwardIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleReorder(index, 'down')}
                            disabled={index === sortedSlides.length - 1 || reordering}
                          >
                            <ArrowDownwardIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {slide.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {slide.subtitle}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{slide.cta}</Typography>
                </TableCell>
                <TableCell>
                  <Box
                    component="img"
                    src={resolveAssetUrl(slide.image)}
                    alt={slide.title}
                    sx={{
                      width: 72,
                      height: 48,
                      borderRadius: 1,
                      objectFit: 'cover',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={Boolean(slide.isActive)}
                    onChange={() => handleToggleActive(slide)}
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="Edit slide">
                      <IconButton size="small" onClick={() => openEditDialog(slide)}>
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete slide">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(slide)}
                      >
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        mb={2}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Manage hero slides displayed on the storefront.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create, edit, reorder, or disable slides. Inactive slides will be hidden from customers.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Add Slide
        </Button>
      </Stack>

      {renderSlidesTable()}

      {sortedSlides.length === 0 && (
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            Fallback slides in use:
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {FALLBACK_SLIDES.map((slide) => (
              <Paper
                key={slide.id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {slide.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {slide.subtitle}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  CTA: {slide.cta}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSlide ? 'Edit Hero Slide' : 'Create Hero Slide'}
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="form"
            onSubmit={handleSubmit}
            id="hero-slide-form"
            sx={{ mt: 1 }}
          >
            <TextField
              label="Title"
              value={formState.title}
              onChange={handleFormChange('title')}
              error={Boolean(formErrors.title)}
              helperText={formErrors.title}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Subtitle"
              value={formState.subtitle}
              onChange={handleFormChange('subtitle')}
              error={Boolean(formErrors.subtitle)}
              helperText={formErrors.subtitle}
              fullWidth
              margin="normal"
              multiline
              minRows={2}
              required
            />
            <TextField
              label="CTA Text"
              value={formState.cta}
              onChange={handleFormChange('cta')}
              error={Boolean(formErrors.cta)}
              helperText={formErrors.cta}
              fullWidth
              margin="normal"
              required
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mt={2}>
              <Box
                component="img"
                src={formState.image ? resolveAssetUrl(formState.image) : '/placeholder.svg'}
                alt="Slide preview"
                sx={{
                  width: 160,
                  height: 100,
                  borderRadius: 1,
                  objectFit: 'cover',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              />
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Image'}
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
                <TextField
                  label="Image Path"
                  value={formState.image}
                  onChange={handleFormChange('image')}
                  error={Boolean(formErrors.image)}
                  helperText={
                    formErrors.image ||
                    'When using uploaded files, the path is filled automatically.'
                  }
                  fullWidth
                  margin="normal"
                  required
                />
              </Box>
            </Stack>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Display Order"
                  type="number"
                  value={formState.order}
                  onChange={handleFormChange('order')}
                  error={Boolean(formErrors.order)}
                  helperText={formErrors.order || 'Lower numbers appear first'}
                  fullWidth
                  inputProps={{ min: 0 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formState.isActive)}
                      onChange={handleFormChange('isActive')}
                    />
                  }
                  label="Slide is active"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            form="hero-slide-form"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Slide'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Settings = () => {
  const { user, fetchUser } = useAuth();

  const [deliverySettings, setDeliverySettings] = useState({
    deliveryMinAmount: '1500',
    deliveryFeeAmount: '100',
  });
  const [deliverySettingsLoading, setDeliverySettingsLoading] = useState(false);
  const [deliverySettingsSaving, setDeliverySettingsSaving] = useState(false);
  const [deliverySettingsDirty, setDeliverySettingsDirty] = useState(false);

  const loadDeliverySettings = useCallback(async () => {
    try {
      setDeliverySettingsLoading(true);
      const result = await settingsService.getDeliverySettings();

      if (result?.success && result?.data) {
        setDeliverySettings({
          deliveryMinAmount: String(
            result.data.deliveryMinAmount ?? 1500
          ),
          deliveryFeeAmount: String(
            result.data.deliveryFeeAmount ?? 100
          ),
        });
        setDeliverySettingsDirty(false);
      }
    } catch (error) {
      console.error('Failed to load delivery settings:', error);
      toast.error(
        error?.response?.data?.message || 'Failed to load delivery settings'
      );
    } finally {
      setDeliverySettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDeliverySettings();
  }, [loadDeliverySettings]);

  const handleDeliveryFieldChange = useCallback(
    (field) => (event) => {
      const value = event.target.value;
      setDeliverySettings((prev) => ({
        ...prev,
        [field]: value,
      }));
      setDeliverySettingsDirty(true);
    },
    []
  );

  const handleSaveDeliverySettings = useCallback(async () => {
    if (deliverySettings.deliveryMinAmount === '') {
      toast.error('Please enter a minimum amount (0 or higher).');
      return;
    }
    if (deliverySettings.deliveryFeeAmount === '') {
      toast.error('Please enter a delivery fee amount (0 or higher).');
      return;
    }

    const minAmount = Number(deliverySettings.deliveryMinAmount);
    const feeAmount = Number(deliverySettings.deliveryFeeAmount);

    if (Number.isNaN(minAmount) || minAmount < 0) {
      toast.error('Please enter a valid minimum amount (0 or higher).');
      return;
    }

    if (Number.isNaN(feeAmount) || feeAmount < 0) {
      toast.error('Please enter a valid delivery fee (0 or higher).');
      return;
    }

    try {
      setDeliverySettingsSaving(true);
      const result = await settingsService.updateDeliverySettings({
        deliveryMinAmount: minAmount,
        deliveryFeeAmount: feeAmount,
      });

      if (result?.success) {
        toast.success(
          result?.message || 'Delivery settings updated successfully'
        );
        if (result?.data) {
          setDeliverySettings({
            deliveryMinAmount: String(
              result.data.deliveryMinAmount ?? minAmount
            ),
            deliveryFeeAmount: String(
              result.data.deliveryFeeAmount ?? feeAmount
            ),
          });
        }
        setDeliverySettingsDirty(false);
      }
    } catch (error) {
      console.error('Failed to update delivery settings:', error);
      toast.error(
        error?.response?.data?.message ||
          'Failed to update delivery settings'
      );
    } finally {
      setDeliverySettingsSaving(false);
    }
  }, [deliverySettings.deliveryFeeAmount, deliverySettings.deliveryMinAmount]);

  const deliveryMinAmountValue = Number(deliverySettings.deliveryMinAmount);
  const deliveryFeeAmountValue = Number(deliverySettings.deliveryFeeAmount);
  const isDeliveryMinAmountInvalid =
    deliverySettings.deliveryMinAmount === '' ||
    Number.isNaN(deliveryMinAmountValue) ||
    deliveryMinAmountValue < 0;
  const isDeliveryFeeInvalid =
    deliverySettings.deliveryFeeAmount === '' ||
    Number.isNaN(deliveryFeeAmountValue) ||
    deliveryFeeAmountValue < 0;

  const SettingCard = ({ title, icon, children }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          {icon}
          <Typography variant="h6" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Settings
      </Typography>

      <Grid container spacing={3}>
        {/* General Settings */}
        <Grid item xs={12} md={6}>
          <SettingCard
            title="Account Settings"
            icon={<AccountCircleIcon color="primary" />}
          >
            <ProfileSettingsForm user={user} onProfileUpdated={fetchUser} />
            <Divider sx={{ my: 3 }} />
            <PasswordSettingsForm />
          </SettingCard>
        </Grid>

        {/* Offer Settings */}
        <Grid item xs={12} md={6}>
          <SettingCard
            title="Offer Settings"
            icon={<LocalOfferIcon color="error" />}
          >
            <OffersTable />
          </SettingCard>
        </Grid>

        {/* Other Settings */}
        <Grid item xs={12} md={6}>
          <SettingCard
            title="Other Settings"
            icon={<SettingsIcon color="action" />}
          >
            <Stack spacing={2}>
              <TextField
                label="Minimum Amount for Free Delivery"
                type="number"
                value={deliverySettings.deliveryMinAmount}
                onChange={handleDeliveryFieldChange('deliveryMinAmount')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
                disabled={deliverySettingsLoading}
                error={isDeliveryMinAmountInvalid}
                helperText="Orders at or above this amount qualify for free delivery."
                fullWidth
              />
              <TextField
                label="Delivery Fee Amount"
                type="number"
                value={deliverySettings.deliveryFeeAmount}
                onChange={handleDeliveryFieldChange('deliveryFeeAmount')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
                disabled={deliverySettingsLoading}
                error={isDeliveryFeeInvalid}
                helperText="Applied when the order total is below the minimum amount."
                fullWidth
              />
              <Box display="flex" justifyContent="flex-end" pt={1}>
                <Button
                  variant="contained"
                  onClick={handleSaveDeliverySettings}
                  disabled={
                    deliverySettingsLoading ||
                    deliverySettingsSaving ||
                    !deliverySettingsDirty ||
                    isDeliveryMinAmountInvalid ||
                    isDeliveryFeeInvalid
                  }
                >
                  {deliverySettingsSaving ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    'Save'
                  )}
                </Button>
              </Box>
            </Stack>
          </SettingCard>
        </Grid>

        {/* Hero Section Settings */}
        <Grid item xs={12}>
          <SettingCard
            title="Hero Section Settings"
            icon={<CollectionsIcon color="primary" />}
          >
            <HeroSectionManager />
          </SettingCard>
        </Grid>
      </Grid>

      {/* Save Button */}
      {/* <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={loading}
          sx={{ px: 4 }}
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box> */}
    </Box>
  );
};

export default Settings;
